import { t } from "@lingui/macro";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";

interface AiQuestionGeneratorProps {
  open: boolean;
  onClose: () => void;
  selfInterviewId?: string;
  studentId: string;
  onQuestionsAdded: () => void;
  onAlert: (message: string, severity: "success" | "error" | "warning" | "info") => void;
}

interface AIGeneratedQuestion {
  id?: string;
  type: string;
  level: string;
  interviewCategory?: string;
  question_text: string;
  option_1: string;
  option_2?: string;
  option_3?: string;
  option_4?: string;
  correct_answer: number[];
  explanation: string;
  tags: string[];
  skill: string;
  selected?: boolean;
}

interface GenerationStatus {
  status: "pending" | "processing" | "completed" | "failed";
  progress: number;
  questions?: AIGeneratedQuestion[];
  error?: string;
  createdAt?: string;
  completedAt?: string;
}

const AiQuestionGenerator: React.FC<AiQuestionGeneratorProps> = ({
  open,
  onClose,
  selfInterviewId,
  studentId,
  onQuestionsAdded,
  onAlert,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    type: "",
    interviewCategory: "",
    skill: [] as string[],
    numberOfQuestions: 1,
    level: "",
    tags: [] as string[],
    additionalContext: "",
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
  const [generatedQuestions, setGeneratedQuestions] = useState<AIGeneratedQuestion[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  const [customCategoryOptions, setCustomCategoryOptions] = useState<Record<string, string[]>>({});
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [customTagOptions, setCustomTagOptions] = useState<Record<string, string[]>>({});
  const [isAddingQuestions, setIsAddingQuestions] = useState(false);
  const [canAddQuestions, setCanAddQuestions] = useState(false);
  const [deletingQuestions, setDeletingQuestions] = useState<Set<string>>(new Set());
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);

  const [addSkillDialogOpen, setAddSkillDialogOpen] = useState(false);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [newSkillInput, setNewSkillInput] = useState("");
  const [newTagInput, setNewTagInput] = useState("");
  const [isAddingNewSkill, setIsAddingNewSkill] = useState(false);
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);

  const [skillDropdownOpen, setSkillDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  const [typeError, setTypeError] = useState(false);
  const [interviewCategoryError, setInterviewCategoryError] = useState(false);
  const [skillError, setSkillError] = useState(false);
  const [levelError, setLevelError] = useState(false);
  const [tagError, setTagError] = useState(false);

  const [originalQuestionCount, setOriginalQuestionCount] = useState(0);
  const [regenerationJobId, setRegenerationJobId] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasShownRegenerationSuccess, setHasShownRegenerationSuccess] = useState(false);
  const skillScrollPositionRef = useRef(0);

  const questionTypes = [
    { value: "short-answer", label: t`Short Answer` }
  ];

  const levels = [
    { value: "beginner", label: t`Beginner` },
    { value: "intermediate", label: t`Intermediate` },
    { value: "advanced", label: t`Advanced` },
  ];

  const interviewCategories = [
    { value: "Technical", label: "Technical" },
    { value: "Behavioral", label: "Behavioral" },
    { value: "HR", label: "HR" },
    { value: "Situational", label: "Situational" },
    { value: "Project-Based", label: "Project-Based" },
    { value: "Leadership", label: "Leadership" },
    { value: "Communication", label: "Communication" },
    { value: "Cultural Fit", label: "Cultural Fit" },
  ];

  const categoryFieldConfig: Record<string, { label: string; options: string[] }> = {
    Technical: {
      label: "Technology / Skill",
      options: [
        "React",
        "Angular",
        "Vue",
        "Next.js",
        "JavaScript",
        "TypeScript",
        "Node.js",
        "Express.js",
        "NestJS",
        "Java",
        "Spring Boot",
        "Python",
        "Django",
        ".NET",
        "SQL",
        "PostgreSQL",
        "MySQL",
        "MongoDB",
        "Redis",
        "REST API",
        "GraphQL",
        "Docker",
        "Kubernetes",
        "AWS",
        "Azure",
        "Git",
        "Microservices",
        "System Design",
        "Data Structures & Algorithms",
        "Testing",
      ],
    },
    Behavioral: {
      label: "Behavioral Competency",
      options: [
        "Teamwork",
        "Conflict Resolution",
        "Problem Solving",
        "Adaptability",
        "Time Management",
        "Ownership",
        "Decision Making",
        "Customer Focus",
      ],
    },
    HR: {
      label: "HR Topic",
      options: [
        "Introduction",
        "Career Goals",
        "Strengths & Weaknesses",
        "Salary",
        "Relocation",
        "Company Knowledge",
        "Motivation",
      ],
    },
    Situational: {
      label: "Scenario Type",
      options: [
        "Production Issues",
        "Deadline Pressure",
        "Team Conflict",
        "Prioritization",
        "Customer Escalation",
        "Incident Response",
      ],
    },
    "Project-Based": {
      label: "Project Area",
      options: [
        "Project Overview",
        "Architecture",
        "Tech Stack",
        "Challenges",
        "Performance Optimization",
        "Security",
        "Deployment",
        "Lessons Learned",
      ],
    },
    Leadership: {
      label: "Leadership Competency",
      options: [
        "Team Management",
        "Mentoring",
        "Delegation",
        "Conflict Management",
        "Stakeholder Management",
        "Project Planning",
      ],
    },
    Communication: {
      label: "Communication Skill",
      options: [
        "Technical Communication",
        "Client Communication",
        "Presentation Skills",
        "Documentation",
        "Negotiation",
        "Feedback Handling",
      ],
    },
    "Cultural Fit": {
      label: "Culture & Values",
      options: [
        "Company Values",
        "Team Collaboration",
        "Learning Mindset",
        "Innovation",
        "Adaptability",
        "Work Ethics",
        "Career Growth",
      ],
    },
  };

  const selectedCategoryConfig = categoryFieldConfig[formData.interviewCategory] || {
    label: "Technology / Skill",
    options: [],
  };
  const selectedCategoryCustomOptions = customCategoryOptions[formData.interviewCategory] || [];
  const selectedCategoryOptions = [
    ...selectedCategoryConfig.options,
    ...selectedCategoryCustomOptions.filter(
      (customOption) =>
        !selectedCategoryConfig.options.some(
          (defaultOption) => defaultOption.toLowerCase() === customOption.toLowerCase()
        )
    ),
  ];

  const DEFAULT_SKILLS = categoryFieldConfig.Technical.options;

  const defaultTagsConfig: Record<string, string[]> = {
    Technical: [
      "Frontend", "Backend", "Full Stack", "API", "Security", "Testing", "Performance", "Architecture",
    ],
    Behavioral: [
      "Teamwork", "Conflict Resolution", "Adaptability", "Ownership", "Time Management", "Growth Mindset",
    ],
    HR: [
      "Compensation", "Career Growth", "Onboarding", "Work-Life Balance", "Diversity", "Culture",
    ],
    Situational: [
      "Decision Making", "Priority", "Stakeholder Management", "Crisis", "Ethics", "Customer Focus",
    ],
    "Project-Based": [
      "Planning", "Execution", "Risk Management", "Budget", "Deadline", "Retrospective",
    ],
    Leadership: [
      "Mentorship", "Vision", "Delegation", "Motivation", "Feedback", "Strategic Thinking",
    ],
    Communication: [
      "Active Listening", "Written", "Verbal", "Presentation", "Negotiation", "Clarity",
    ],
    "Cultural Fit": [
      "Values", "Collaboration", "Autonomy", "Innovation", "Community", "Mission",
    ],
  };

  const selectedCategoryDefaultTags = defaultTagsConfig[formData.interviewCategory] || [];
  const selectedCategoryCustomTags = customTagOptions[formData.interviewCategory] || [];
  const selectedCategoryAllTags = [
    ...selectedCategoryDefaultTags,
    ...selectedCategoryCustomTags.filter(
      (customTag) =>
        !selectedCategoryDefaultTags.some(
          (defaultTag) => defaultTag.toLowerCase() === customTag.toLowerCase()
        )
    ),
  ];

  useEffect(() => {
    if (!open) return;
    const fetchSkills = async () => {
      setSkillsLoading(true);
      try {
        if (formData.interviewCategory !== "Technical") {
          setAvailableSkills(selectedCategoryOptions);
          return;
        }

        const response = await axios.get("/exam/api/question/skills/unique?context=interview");
        const backendSkills = response.data.result || [];

        const allSkills = [...DEFAULT_SKILLS];

        backendSkills.forEach((skill: string) => {
          const isDuplicate = allSkills.some(
            (existingSkill) => existingSkill.toLowerCase() === skill.toLowerCase()
          );
          if (!isDuplicate) {
            allSkills.push(skill);
          }
        });

        selectedCategoryCustomOptions.forEach((skill) => {
          const isDuplicate = allSkills.some(
            (existingSkill) => existingSkill.toLowerCase() === skill.toLowerCase()
          );
          if (!isDuplicate) {
            allSkills.push(skill);
          }
        });

        setAvailableSkills(allSkills);
      } catch (error) {
        console.error("Error fetching skills:", error);
        setAvailableSkills(DEFAULT_SKILLS);
      } finally {
        setSkillsLoading(false);
      }
    };
    if (open && formData.interviewCategory) {
      fetchSkills();
    } else if (open) {
      setAvailableSkills([]);
    }
  }, [open, formData.interviewCategory, customCategoryOptions]);

  useEffect(() => {
    if (!open) return;
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const response = await axios.get("/exam/api/question/tags/unique?context=interview");
        const backendTags = response.data.result || [];

        const allTags = [...selectedCategoryAllTags];

        backendTags.forEach((tag: string) => {
          const isDuplicate = allTags.some(
            (existingTag) => existingTag.toLowerCase() === tag.toLowerCase()
          );
          if (!isDuplicate) {
            allTags.push(tag);
          }
        });

        setAvailableTags(allTags);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setAvailableTags(selectedCategoryAllTags);
      } finally {
        setTagsLoading(false);
      }
    };
    if (open && formData.interviewCategory) {
      fetchTags();
    } else if (open) {
      setAvailableTags([]);
    }
  }, [open, formData.interviewCategory, customTagOptions]);

  const ensureQuestionStructure = (questions: any[]) => {
    return questions.map((q: any, i: number) => {
      const level = q.level || formData.level || "intermediate";
      return {
        ...q,
        id: `ai-${Date.now()}-${i}`,
        level: level,
      };
    });
  };

  useEffect(() => {
    if (!(regenerationJobId || jobId) || !(isRegenerating || isGenerating)) return;

    const interval = setInterval(async () => {
      try {
        const currentJobId = regenerationJobId || jobId;
        const res = await axios.get(`/api/integration/external-service/ai/status/${currentJobId}`);
        const status = res.data.data || res.data.result || res.data;
        setGenerationStatus(status);

        if (status.status === "completed") {
          if (isRegenerating && regenerationJobId) {
            if (hasShownRegenerationSuccess) {
              clearInterval(interval);
              return;
            }

            try {
              await axios.post(`/api/integration/external-service/ai/merge/${jobId}/${regenerationJobId}`);

              const updatedRes = await axios.get(`/api/integration/external-service/ai/status/${jobId}`);
              const updatedStatus = updatedRes.data.data || updatedRes.data.result || updatedRes.data;

              const questions = ensureQuestionStructure(updatedStatus.questions || []);

              setGeneratedQuestions(questions);
              setOriginalQuestionCount(questions.length);
              setIsRegenerating(false);
              setRegenerationJobId(null);
              setCanAddQuestions(true);
              setHasShownRegenerationSuccess(true);

              onAlert(`Successfully regenerated questions! Total: ${questions.length}`, "success");
            } catch (mergeError) {
              console.error("Error merging questions:", mergeError);
              onAlert("Failed to merge regenerated questions", "error");
              setIsRegenerating(false);
              setCanAddQuestions(true);
            }
          } else {
            const questions = ensureQuestionStructure(status.questions || []);
            setGeneratedQuestions(questions);
            setOriginalQuestionCount(questions.length);
            setIsGenerating(false);
            setCanAddQuestions(true);
            onAlert(`Successfully generated ${questions.length} questions!`, "success");
          }
        } else if (status.status === "failed") {
          if (isRegenerating) {
            setIsRegenerating(false);
            setRegenerationJobId(null);
          } else {
            setIsGenerating(false);
          }
          setCanAddQuestions(false);
          onAlert(`Generation failed: ${status.error || "Unknown error"}`, "error");
        }
      } catch (error) {
        console.error("Error polling status:", error);
        if (isRegenerating) {
          setIsRegenerating(false);
          setRegenerationJobId(null);
        } else {
          setIsGenerating(false);
        }
        setCanAddQuestions(false);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, regenerationJobId, isGenerating, isRegenerating, hasShownRegenerationSuccess, formData.level]);

  const handleCloseDrawer = () => {
    resetFormState();
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      if (field === "interviewCategory") {
        return { ...prev, interviewCategory: value, skill: [], tags: [] };
      }
      return { ...prev, [field]: value };
    });
    if (field === "type") setTypeError(false);
    if (field === "interviewCategory") {
      setInterviewCategoryError(false);
      setSkillError(false);
      setTagError(false);
    }
    if (field === "skill") setSkillError(false);
    if (field === "level") setLevelError(false);
    if (field === "tags") setTagError(false);
  };

  const toCamelCase = (str: string): string => {
    const trimmed = str.trim();
    if (!trimmed.includes(" ") && !trimmed.includes("-") && !trimmed.includes("_")) {
      return trimmed.toLowerCase();
    }
    return trimmed
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
  };

  const isDuplicate = (newItem: string, existingItems: string[]): boolean => {
    const normalizedNew = toCamelCase(newItem);
    return existingItems.some((item) => toCamelCase(item) === normalizedNew);
  };

  const findExistingItem = (newItem: string, existingItems: string[]): string | null => {
    const normalizedNew = toCamelCase(newItem);
    return existingItems.find((item) => toCamelCase(item) === normalizedNew) || null;
  };

  const handleAddSkill = async (newSkill: string) => {
    const trimmedSkill = newSkill.trim();
    const category = formData.interviewCategory;
    const fieldLabel = selectedCategoryConfig.label;

    if (!category) {
      onAlert("Please select an interview category first", "warning");
      return trimmedSkill;
    }

    const existingSkill = findExistingItem(trimmedSkill, availableSkills);

    if (existingSkill) {
      onAlert(`${fieldLabel} "${existingSkill}" already exists!`, "info");
      return existingSkill;
    }

    setCustomCategoryOptions((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), trimmedSkill],
    }));
    setAvailableSkills((prev) => [...prev, trimmedSkill]);
    onAlert(`New ${fieldLabel.toLowerCase()} "${trimmedSkill}" added successfully!`, "success");

    return trimmedSkill;
  };

  const handleAddTag = async (newTag: string) => {
    const trimmedTag = newTag.trim();
    const category = formData.interviewCategory;

    if (!category) {
      onAlert("Please select an interview category first", "warning");
      return trimmedTag;
    }

    const existingTag = findExistingItem(trimmedTag, availableTags);

    if (existingTag) {
      onAlert(`Tag "${existingTag}" already exists!`, "info");
      return existingTag;
    }

    setCustomTagOptions((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), trimmedTag],
    }));
    setAvailableTags((prev) => [...prev, trimmedTag]);
    onAlert(`New tag "${trimmedTag}" added successfully!`, "success");

    return trimmedTag;
  };

  const handleAddNewSkillFromDialog = async () => {
    if (!newSkillInput.trim()) {
      onAlert(`Please enter ${selectedCategoryConfig.label.toLowerCase()}`, "warning");
      return;
    }

    setIsAddingNewSkill(true);
    try {
      const addedSkill = await handleAddSkill(newSkillInput.trim());
      const updatedSkills = formData.skill.some(
        (skill) => skill.toLowerCase() === addedSkill.toLowerCase()
      )
        ? formData.skill
        : [...formData.skill, addedSkill];

      handleInputChange("skill", updatedSkills);
      setNewSkillInput("");
      setAddSkillDialogOpen(false);
    } catch (error) {
      console.error("Error adding skill:", error);
    } finally {
      setIsAddingNewSkill(false);
    }
  };

  const handleAddNewTagFromDialog = async () => {
    if (!newTagInput.trim()) {
      onAlert("Please enter a tag name", "warning");
      return;
    }

    setIsAddingNewTag(true);
    try {
      const addedTag = await handleAddTag(newTagInput.trim());
      handleInputChange("tags", [...formData.tags, addedTag]);
      setNewTagInput("");
      setAddTagDialogOpen(false);
    } catch (error) {
      console.error("Error adding tag:", error);
    } finally {
      setIsAddingNewTag(false);
    }
  };

  const handleGenerateQuestions = async () => {
    let hasError = false;

    if (!formData.type) {
      setTypeError(true);
      hasError = true;
    } else {
      setTypeError(false);
    }

    if (!formData.skill || formData.skill.length === 0) {
      setSkillError(true);
      hasError = true;
    } else {
      setSkillError(false);
    }

    if (!formData.interviewCategory) {
      setInterviewCategoryError(true);
      hasError = true;
    } else {
      setInterviewCategoryError(false);
    }

    if (!formData.tags || formData.tags.length === 0) {
      setTagError(true);
      hasError = true;
    } else {
      setTagError(false);
    }

    if (!formData.level) {
      setLevelError(true);
      hasError = true;
    } else {
      setLevelError(false);
    }

    if (formData.numberOfQuestions > 25) {
      onAlert("Number of questions should not be more than 25", "error");
      return;
    }

    if (hasError) {
      return;
    }

    setIsGenerating(true);
    setGeneratedQuestions([]);
    setGenerationStatus(null);
    setJobId(null);
    setCanAddQuestions(false);
    setHasShownRegenerationSuccess(false);

    try {
      const filteredTags = formData.tags.filter(
        (tag) =>
          !formData.skill.some((skill) => skill.toLowerCase() === tag.toLowerCase()) &&
          tag.toLowerCase() !== formData.level.toLowerCase()
      );

      const payload = {
        type: formData.type,
        category: formData.interviewCategory,
        skill: formData.skill,
        numberOfQuestions: formData.numberOfQuestions,
        level: formData.level,
        tags: filteredTags.length > 0 ? filteredTags : formData.tags,
        additionalContext: formData.additionalContext,
        studentId,
      };

      const res = await axios.post(
        `/api/integration/external-service/ai/generate/self-interview/${selfInterviewId}`,
        payload
      );
      const newJobId = res.data.result?.jobId || res.data.jobId;
      setJobId(newJobId);
      onAlert("AI question generation started!", "info");
    } catch (error: any) {
      setIsGenerating(false);
      onAlert(error.response?.data?.message || "Failed to generate questions", "error");
    }
  };

  const handleAddSingleQuestion = async (question: AIGeneratedQuestion, index: number) => {
    if (!jobId) {
      onAlert("Job ID is missing. Please regenerate questions.", "error");
      return;
    }

    if (generationStatus?.status !== "completed") {
      onAlert("Questions must be fully generated before adding.", "warning");
      return;
    }

    setIsAddingQuestions(true);
    try {
      await axios.post(
        `/api/integration/external-service/ai/add-single-to-self-interview/${selfInterviewId}/${jobId}/${index}`,
        { studentId }
      );
      onAlert("Question added successfully!", "success");
      onQuestionsAdded();
      setGeneratedQuestions((prev) => prev.filter((q) => q.id !== question.id));
      setOriginalQuestionCount((prev) => prev - 1);
      if (generatedQuestions.length === 1) {
        setTimeout(onClose, 800);
      }
    } catch (error: any) {
      onAlert(error.response?.data?.message || "Failed to add question", "error");
    } finally {
      setIsAddingQuestions(false);
    }
  };

  const handleAddAllQuestions = async () => {
    if (!jobId) {
      onAlert("Job ID is missing. Please regenerate questions.", "error");
      return;
    }

    if (generationStatus?.status !== "completed") {
      onAlert("Questions must be fully generated before adding.", "warning");
      return;
    }

    setIsAddingQuestions(true);
    try {
      await axios.post(
        `/api/integration/external-service/ai/add-to-self-interview/${selfInterviewId}/${jobId}`,
        { studentId }
      );
      onAlert(`All ${generatedQuestions.length} questions added!`, "success");
      onQuestionsAdded();
      resetFormState();
      setTimeout(onClose, 800);
    } catch (error: any) {
      console.error("Error adding questions:", error.response?.data || error);
      onAlert(error.response?.data?.message || "Failed to add questions", "error");
    } finally {
      setIsAddingQuestions(false);
    }
  };

  const resetFormState = () => {
    setFormData({
      type: "",
      interviewCategory: "",
      skill: [],
      numberOfQuestions: 1,
      level: "",
      tags: [],
      additionalContext: "",
    });
    setGeneratedQuestions([]);
    setJobId(null);
    setOriginalQuestionCount(0);
    setGenerationStatus(null);
    setRegenerationJobId(null);
    setIsRegenerating(false);
    setHasShownRegenerationSuccess(false);
    setCanAddQuestions(false);
    setTypeError(false);
    setInterviewCategoryError(false);
    setSkillError(false);
    setLevelError(false);
    setTagError(false);
    setDeletingQuestions(new Set());
    setNewSkillInput("");
    setNewTagInput("");
  };

  const handleDeleteQuestion = async (question: AIGeneratedQuestion, index: number) => {
    if (!question.id) return;

    if (!jobId) {
      onAlert("Job ID is missing. Cannot delete question.", "error");
      return;
    }

    setDeletingQuestions((prev) => new Set(prev).add(question.id!));
    try {
      await axios.delete(`/api/integration/external-service/ai/delete/${jobId}/${index}`);
      setGeneratedQuestions((prev) => prev.filter((q) => q.id !== question.id));
      onAlert("Question removed successfully!", "success");

      if (generatedQuestions.length === 1) {
        handleGenerateMore();
      }
    } catch (error: any) {
      onAlert(error.response?.data?.message || "Failed to remove question", "error");
    } finally {
      setDeletingQuestions((prev) => {
        const s = new Set(prev);
        s.delete(question.id!);
        return s;
      });
    }
  };

  const handleGenerateMore = async () => {
    if (!jobId) {
      onAlert("No active generation job found", "error");
      return;
    }

    const deletedCount = originalQuestionCount - generatedQuestions.length;

    if (deletedCount <= 0) {
      setGeneratedQuestions([]);
      setGenerationStatus(null);
      setJobId(null);
      setCanAddQuestions(false);
      setOriginalQuestionCount(0);
      return;
    }

    setIsRegenerating(true);
    setCanAddQuestions(false);
    setHasShownRegenerationSuccess(false);

    try {
      const res = await axios.post(`/api/integration/external-service/ai/regenerate/${jobId}`, {
        numberOfQuestions: deletedCount,
      });
      setRegenerationJobId(res.data.jobId || res.data.result?.jobId);
      onAlert(`Regenerating ${deletedCount} question(s)...`, "info");
    } catch (error: any) {
      setIsRegenerating(false);
      console.error("Regeneration error:", error);
      onAlert(error.response?.data?.message || "Failed to regenerate questions", "error");
    }
  };

  const formatCamelCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const showQuestions = generatedQuestions.length > 0 && !isGenerating;

  return (
    <>
      <Dialog
        open={addSkillDialogOpen}
        onClose={() => setAddSkillDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            position: "absolute",
            width: "350px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "16px", py: 1.5 }}>
          Add New {selectedCategoryConfig.label}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Skill Name"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={newSkillInput}
            onChange={(e) => setNewSkillInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddNewSkillFromDialog();
              }
            }}
            placeholder={`Add ${selectedCategoryConfig.label.toLowerCase()}`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddSkillDialogOpen(false)}
            color="inherit"
            size="small"
          >
            {t`Cancel`}
          </Button>
          <Button
            onClick={handleAddNewSkillFromDialog}
            variant="contained"
            color="primary"
            size="small"
            disabled={isAddingNewSkill}
          >
            {isAddingNewSkill ? "Adding..." : `Add ${selectedCategoryConfig.label}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addTagDialogOpen}
        onClose={() => setAddTagDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            position: "absolute",
            width: "350px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "16px", py: 1.5 }}>
          Add New Tag
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddNewTagFromDialog();
              }
            }}
            placeholder="e.g., Frontend, Backend, Database"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddTagDialogOpen(false)}
            color="inherit"
            size="small"
          >
            {t`Cancel`}
          </Button>
          <Button
            onClick={handleAddNewTagFromDialog}
            variant="contained"
            color="primary"
            size="small"
            disabled={isAddingNewTag}
          >
            {isAddingNewTag ? "Adding..." : "Add Tag"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        transitionDuration={{ enter: 800, exit: 800 }}
      >
        <Box sx={{ width: { sm: "100vw", md: "40vw" } }}>
          <Box
            sx={{
              backgroundColor: "primary.dark",
              p: "5px 10px",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "33px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyIcon fontSize="small" />
              {t`Generate Questions with AI`}
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ color: "grey.400", "&:hover": { color: "#f94449" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflow: "auto" }}>
            {!showQuestions && !isGenerating && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" required fullWidth error={typeError}>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={(e) => handleInputChange("type", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                        {t`Select Question Type *`}
                      </MenuItem>
                      {questionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {typeError && (
                      <Typography color="error" variant="caption">
                        Question type is required.
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" required fullWidth error={levelError}>
                    <Select
                      name="level"
                      value={formData.level}
                      onChange={(e) => handleInputChange("level", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                        {t`Select Level *`}
                      </MenuItem>
                      {levels.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {levelError && (
                      <Typography color="error" variant="caption">
                        Level selection is required.
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    size="small"
                    required
                    fullWidth
                    error={interviewCategoryError}
                  >
                    <Select
                      name="interviewCategory"
                      value={formData.interviewCategory}
                      onChange={(e) =>
                        handleInputChange("interviewCategory", e.target.value)
                      }
                      displayEmpty
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                        Select Interview Category *
                      </MenuItem>
                      {interviewCategories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {interviewCategoryError && (
                      <Typography color="error" variant="caption">
                        Interview category is required.
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Autocomplete
                    multiple
                    options={availableSkills}
                    value={formData.skill}
                    disabled={!formData.interviewCategory}
                    open={skillDropdownOpen}
                    onOpen={() => setSkillDropdownOpen(true)}
                    onClose={() => setSkillDropdownOpen(false)}
                    onChange={async (event, newValue) => {
                      handleInputChange("skill", newValue);
                    }}
                    loading={skillsLoading}
                    disableCloseOnSelect
                    disableClearable
                    getOptionLabel={(option) => option}
                    noOptionsText={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No options
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddSkillDialogOpen(true);
                            setSkillDropdownOpen(false);
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                          }}
                        >
                          Add New {selectedCategoryConfig.label}
                        </Button>
                      </Box>
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option}
                            {...tagProps}
                            size="small"
                            sx={{ fontSize: "12px" }}
                          />
                        );
                      })
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    ListboxComponent={(props) => {
                      const { children, ...other } = props;
                      return (
                        <div
                          style={{
                            maxHeight: 300,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <ul
                            {...other}
                            ref={(node) => {
                              if (node) {
                                node.scrollTop = skillScrollPositionRef.current;
                              }
                            }}
                            onScroll={(e) => {
                              skillScrollPositionRef.current = (
                                e.currentTarget as HTMLUListElement
                              ).scrollTop;
                            }}
                            style={{
                              margin: 0,
                              padding: 0,
                              listStyle: "none",
                              overflow: "auto",
                              flexGrow: 1,
                            }}
                          >
                            {children}
                          </ul>
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setAddSkillDialogOpen(true);
                            }}
                            style={{
                              position: "sticky",
                              bottom: 0,
                              backgroundColor: theme.palette.primary.dark,
                              color: "#ffffff",
                              padding: "10px 16px",
                              cursor: "pointer",
                              borderTop: "1px solid #ccc",
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <AddIcon style={{ marginRight: 8 }} />
                            Add New {selectedCategoryConfig.label}
                          </div>
                        </div>
                      );
                    }}
                    renderInput={(params) => {
                      const customParams = {
                        ...params,
                        InputProps: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {skillDropdownOpen && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSkillDropdownOpen(false);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    right: 30,
                                    padding: "4px",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          ),
                        },
                      };

                      return (
                        <TextField
                          {...customParams}
                          name="skill"
                          variant="filled"
                          label={selectedCategoryConfig.label}
                          placeholder={
                            formData.skill?.length > 0
                              ? ""
                              : formData.interviewCategory
                                ? `Select ${selectedCategoryConfig.label.toLowerCase()}`
                                : "Select interview category first"
                          }
                          required
                          error={skillError}
                          helperText={
                            skillError
                              ? `At least one ${selectedCategoryConfig.label.toLowerCase()} is required`
                              : ""
                          }
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <TextField
                    name="numberOfQuestions"
                    type="number"
                    variant="filled"
                    label={t`Number of Questions`}
                    value={formData.numberOfQuestions}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      handleInputChange(
                        "numberOfQuestions",
                        Math.min(25, Math.max(1, value))
                      );
                    }}
                    inputProps={{ min: 1, max: 25 }}
                    error={formData.numberOfQuestions > 25}
                    helperText={
                      formData.numberOfQuestions > 25
                        ? "Number of questions should not be more than 25"
                        : ""
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Autocomplete
                    multiple
                    options={availableTags}
                    value={formData.tags}
                    open={tagDropdownOpen}
                    onOpen={() => setTagDropdownOpen(true)}
                    onClose={() => setTagDropdownOpen(false)}
                    onChange={async (event, newValue) => {
                      handleInputChange("tags", newValue);
                    }}
                    loading={tagsLoading}
                    disableCloseOnSelect
                    disableClearable
                    getOptionLabel={(option) => option}
                    noOptionsText={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No options
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddTagDialogOpen(true);
                            setTagDropdownOpen(false);
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                          }}
                        >
                          Add New Tag
                        </Button>
                      </Box>
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option}
                            {...tagProps}
                            size="small"
                            sx={{ fontSize: "12px" }}
                          />
                        );
                      })
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    ListboxComponent={(props) => {
                      const { children, ...other } = props;
                      return (
                        <div
                          style={{
                            maxHeight: 300,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <ul
                            {...other}
                            ref={(node) => {
                              if (node) {
                                node.scrollTop = skillScrollPositionRef.current;
                              }
                            }}
                            onScroll={(e) => {
                              skillScrollPositionRef.current = (
                                e.currentTarget as HTMLUListElement
                              ).scrollTop;
                            }}
                            style={{
                              margin: 0,
                              padding: 0,
                              listStyle: "none",
                              overflow: "auto",
                              flexGrow: 1,
                            }}
                          >
                            {children}
                          </ul>
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setAddTagDialogOpen(true);
                            }}
                            style={{
                              position: "sticky",
                              bottom: 0,
                              backgroundColor: theme.palette.primary.dark,
                              color: "#ffffff",
                              padding: "10px 16px",
                              cursor: "pointer",
                              borderTop: "1px solid #ccc",
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <AddIcon style={{ marginRight: 8 }} />
                            Add New Tag
                          </div>
                        </div>
                      );
                    }}
                    renderInput={(params) => {
                      const customParams = {
                        ...params,
                        InputProps: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {tagDropdownOpen && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTagDropdownOpen(false);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    right: 30,
                                    padding: "4px",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          ),
                        },
                      };

                      return (
                        <TextField
                          {...customParams}
                          name="tags"
                          variant="filled"
                          label={t`Tags`}
                          placeholder={
                            formData.tags?.length > 0
                              ? ""
                              : "Select tags or add new tag"
                          }
                          required
                          error={tagError}
                          helperText={
                            tagError ? "At least one tag is required" : ""
                          }
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ my: 1 }}>
                  <TextField
                    name="additionalContext"
                    label={t`Additional Context`}
                    multiline
                    rows={4}
                    variant="filled"
                    placeholder="Any specific requirements...e.g., Focus on Python data analysis and automation"
                    value={formData.additionalContext}
                    onChange={(e) =>
                      handleInputChange("additionalContext", e.target.value)
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
            )}

            {isGenerating && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {t`Generating Questions...`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Creating {formData.numberOfQuestions} short-answer questions
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={generationStatus?.progress || 0}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {generationStatus?.progress || 0}% {t`Complete`}
                </Typography>
                {generationStatus?.status && (
                  <Chip
                    label={generationStatus.status.toUpperCase()}
                    color={
                      generationStatus.status === "processing"
                        ? "warning"
                        : generationStatus.status === "completed"
                          ? "success"
                          : "default"
                    }
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
            )}

            {showQuestions && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" color="primary">
                    {t`Generated Questions`} ({generatedQuestions.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={handleAddAllQuestions}
                      startIcon={<AddIcon />}
                      variant="contained"
                      color="success"
                      sx={{ fontSize: "13px", textTransform: "none" }}
                      disabled={
                        isAddingQuestions ||
                        !canAddQuestions ||
                        generatedQuestions.length === 0
                      }
                    >
                      {t`Add All`}
                    </Button>
                    <Button
                      size="small"
                      onClick={handleGenerateMore}
                      startIcon={<RefreshIcon />}
                      variant="outlined"
                      sx={{ fontSize: "13px", textTransform: "none" }}
                      disabled={isRegenerating}
                    >
                      {originalQuestionCount > generatedQuestions.length
                        ? `${t`Regenerate`} ${originalQuestionCount - generatedQuestions.length} Question(s)`
                        : "Generate Again"}
                    </Button>
                  </Box>
                </Box>

                {isRegenerating && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t`Regenerating`} {originalQuestionCount - generatedQuestions.length}{" "}
                    {t`question(s)...`}
                  </Alert>
                )}

                {!canAddQuestions && !isRegenerating && (
                  <Alert severity="warning" sx={{ mb: 2, fontSize: "13px" }}>
                    Question regeneration is in progress.
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {generatedQuestions.map((question, index) => (
                    <Grid item xs={12} key={question.id}>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            minHeight: "33px",
                          }}
                        >
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            <Typography
                              sx={{
                                boxShadow: "0px 0px 2px 0px gray",
                                pt: 1,
                                pb: "0px",
                                borderRadius: "5px",
                                px: "10px",
                                display: "flex",
                                gap: "5px",
                                fontSize: "12px",
                                alignItems: "center",
                                height: "32px",
                              }}
                            >
                              {`${index + 1}. Short Answer`}
                            </Typography>
                            <Typography
                              sx={{
                                boxShadow: "0px 0px 2px 0px gray",
                                pt: 1,
                                pb: "0px",
                                borderRadius: "5px",
                                px: "12px",
                                display: "flex",
                                gap: "5px",
                                fontSize: "12px",
                                height: "32px",
                                alignItems: "center",
                              }}
                            >
                              <BarChartIcon sx={{ fontSize: "15px" }} />
                              {formatCamelCase(question.level)}
                            </Typography>
                          </Box>
                          <Box display={"flex"} gap={1}>
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{
                                textTransform: "none",
                                py: "1px",
                                px: "10px",
                                height: "29px",
                                fontSize: "10px",
                              }}
                              onClick={() =>
                                handleAddSingleQuestion(question, index)
                              }
                              disabled={isAddingQuestions || !canAddQuestions}
                            >
                              <AddIcon sx={{ fontSize: "15px", mr: "5px" }} />
                              {t`Add`}
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteQuestion(question, index)
                              }
                              disabled={deletingQuestions.has(question.id!)}
                              sx={{ height: "29px", width: "29px" }}
                            >
                              <DeleteIcon sx={{ fontSize: "15px" }} />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box mt={2}>
                          <Typography
                            sx={{ fontWeight: "bold", fontSize: "14px" }}
                          >
                            {question.question_text}
                          </Typography>
                          {question.option_1 && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                              {[
                                [
                                  { option: question.option_1, index: 1 },
                                  { option: question.option_2, index: 2 },
                                ],
                                [
                                  { option: question.option_3, index: 3 },
                                  { option: question.option_4, index: 4 },
                                ],
                              ].map((row, rowIndex) => (
                                <Grid container spacing={2} key={rowIndex}>
                                  {row.map(
                                    ({ option, index: optIndex }) =>
                                      option && (
                                        <Grid item xs={6} key={optIndex}>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              m: 0.5,
                                            }}
                                          >
                                            {question.correct_answer.includes(
                                              optIndex
                                            ) ? (
                                              <CheckCircleIcon
                                                color="success"
                                                sx={{ fontSize: "20px" }}
                                              />
                                            ) : (
                                              <CloseIcon
                                                color="error"
                                                sx={{ fontSize: "20px" }}
                                              />
                                            )}
                                            <Typography
                                              sx={{ fontSize: "13px" }}
                                            >
                                              {option}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )
                                  )}
                                </Grid>
                              ))}
                            </Box>
                          )}
                        </Box>

                        <Divider variant="middle" sx={{ my: "7px" }} />

                        <Typography
                          sx={{
                            color: "gray",
                            mb: "5px",
                            mt: 2,
                            fontWeight: "bold",
                            fontSize: "13px",
                          }}
                        >
                          {t`Answer explanation`}
                        </Typography>
                        <Typography sx={{ fontSize: "13px" }}>
                          {question.explanation}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>

          {!showQuestions && !isGenerating && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 3,
                pb: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateQuestions}
                sx={{ fontSize: "13px", px: 2, py: 0.5, textTransform: "none" }}
              >
                {t`Generate Questions with AI`}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default AiQuestionGenerator;
